import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const NavBar = styled.nav`
  background-color: white;
  padding: 1.2rem 2rem;
  position: sticky;
  width: 100%;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const NavContainer = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
`;

const Logo = styled(Link)`
  font-size: 1.8rem;
  font-weight: 700;
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  height: 48px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    height: 40px;
  }
`;

const LogoText = styled.span`
  background: linear-gradient(90deg, #291759, #6C63FF);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  line-height: 1;
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;
  height: 100%;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownMenu = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  background-color: white;
  min-width: 220px;
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  padding: 0.75rem 0;
  z-index: 101;
  border-radius: 12px;
  transition: all 0.3s ease;
  pointer-events: none;
  margin-top: 0.5rem;
  
  &:before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 16px;
    height: 16px;
    background-color: white;
    border-radius: 2px;
  }
`;

const NavItem = styled.div`
  position: relative;
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  
  &:hover > ${DropdownMenu} {
    visibility: visible;
    opacity: 1;
    transform: translateX(-50%) translateY(0);
    pointer-events: auto;
  }
`;

const DropdownLink = styled(Link)`
  display: block;
  padding: 0.7rem 1.5rem;
  text-decoration: none;
  color: #333;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  text-align: left;
  
  &:hover {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
    padding-left: 1.8rem;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-size: 1.1rem;
  font-weight: 500;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      width: 100%;
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
`;

const DropdownNavLink = styled.div`
  text-decoration: none;
  color: #333;
  font-size: 1.1rem;
  font-weight: 500;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      width: 100%;
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  height: 40px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SignInLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 600;
  font-size: 1rem;
  background-color: transparent;
  padding: 0.6rem 1.2rem;
  border-radius: ${props => props.theme.borderRadius?.pill || '50px'};
  border: 1px solid ${props => props.theme.colors?.border || '#e0e0e0'};
  transition: all 0.3s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  box-sizing: border-box;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    background-color: rgba(41, 23, 89, 0.05);
    transform: translateY(-2px);
  }
`;

const JoinButton = styled(Link)`
  background-color: ${props => props.theme.colors?.primary || '#291759'};
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: ${props => props.theme.borderRadius?.pill || '50px'};
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  box-shadow: 0 4px 10px rgba(41, 23, 89, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  box-sizing: border-box;
  
  &:hover {
    background-color: ${props => props.theme.colors?.primaryLight || '#3a2277'};
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(41, 23, 89, 0.25);
  }
`;

const ChevronIcon = styled.span`
  display: inline-block;
  margin-left: 5px;
  transition: transform 0.2s ease;
  line-height: 1;
  
  ${NavItem}:hover & {
    transform: rotate(180deg);
  }
`;

// Mobile menu components
const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 24px;
  position: relative;
  z-index: 999;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const HamburgerIcon = styled.div`
  width: 30px;
  height: 3px;
  background-color: ${props => props.isOpen ? 'transparent' : '#333'};
  transition: all 0.3s ease;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    width: 30px;
    height: 3px;
    background-color: #333;
    transition: all 0.3s ease;
    left: 0;
  }
  
  &:before {
    top: ${props => props.isOpen ? '0' : '-10px'};
    transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
  
  &:after {
    top: ${props => props.isOpen ? '0' : '10px'};
    transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: white;
  z-index: 998;
  padding: 80px 2rem 2rem;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MobileNavItem = styled.div`
  width: 100%;
`;

const MobileNavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-size: 1.3rem;
  font-weight: 500;
  display: block;
  padding: 0.8rem 0;
  border-bottom: 1px solid #eee;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileDropdownNavLink = styled.div`
  text-decoration: none;
  color: #333;
  font-size: 1.3rem;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileDropdownMenu = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding-left: 1.5rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const MobileAuthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      document.body.style.overflow = 'auto';
    } else {
      document.body.style.overflow = 'hidden';
    }
  };
  
  const toggleDropdown = (name) => {
    if (openDropdown === name) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(name);
    }
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
  };
  
  return (
    <NavBar>
      <NavContainer>
        <Logo to="/">
          <LogoText>GeoDiet</LogoText>
        </Logo>
        
        <NavCenter>
          <NavLinks>
            <NavItem>
              <DropdownNavLink>
                Shop
                <ChevronIcon>▾</ChevronIcon>
              </DropdownNavLink>
              <DropdownMenu>
                <DropdownLink to="/shop/all-plans">All Plans</DropdownLink>
              </DropdownMenu>
            </NavItem>
            
            <NavItem>
              <DropdownNavLink>
                Wonder Kliniker
                <ChevronIcon>▾</ChevronIcon>
              </DropdownNavLink>
              <DropdownMenu>
                <DropdownLink to="/nutritionists/find">Hitta din Wonder Klinik</DropdownLink>
                <DropdownLink to="/become-nutritionist">Bli en Wonder Klinik</DropdownLink>
                <DropdownLink to="/nutritionists/experts">Om Wonder teknologin</DropdownLink>
              </DropdownMenu>
            </NavItem>
            
            <NavLink to="/journal">Journal</NavLink>
            <NavLink to="/OurStory">Our Story</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </NavLinks>
        </NavCenter>
        
        <AuthSection>
          <SignInLink to="/auth/login">SIGN IN</SignInLink>
          <JoinButton to="/auth/partner-signup">JOIN AS PARTNER</JoinButton>
        </AuthSection>
        
        <MobileMenuToggle onClick={toggleMenu} aria-label="Toggle menu">
          <HamburgerIcon isOpen={isMenuOpen} />
        </MobileMenuToggle>
        
        <MobileMenu isOpen={isMenuOpen}>
          <MobileNavLinks>
            <MobileNavItem>
              <MobileDropdownNavLink onClick={() => toggleDropdown('shop')}>
                Shop
                <ChevronIcon style={{ transform: openDropdown === 'shop' ? 'rotate(180deg)' : 'rotate(0)' }}>▾</ChevronIcon>
              </MobileDropdownNavLink>
              <MobileDropdownMenu isOpen={openDropdown === 'shop'}>
                <MobileNavLink to="/shop/all-plans" onClick={closeMenu}>All Plans</MobileNavLink>
              </MobileDropdownMenu>
            </MobileNavItem>
            
            <MobileNavItem>
              <MobileDropdownNavLink onClick={() => toggleDropdown('nutritionists')}>
                Wonder Kliniker
                <ChevronIcon style={{ transform: openDropdown === 'nutritionists' ? 'rotate(180deg)' : 'rotate(0)' }}>▾</ChevronIcon>
              </MobileDropdownNavLink>
              <MobileDropdownMenu isOpen={openDropdown === 'nutritionists'}>
                <MobileNavLink to="/nutritionists/find" onClick={closeMenu}>Hitta din Wonder Klinik</MobileNavLink>
                <MobileNavLink to="/become-nutritionist" onClick={closeMenu}>Bli en Wonder Klinik</MobileNavLink>
                <MobileNavLink to="/nutritionists/experts" onClick={closeMenu}>Om Wonder teknologin</MobileNavLink>
              </MobileDropdownMenu>
            </MobileNavItem>
            
            <MobileNavItem>
              <MobileNavLink to="/journal" onClick={closeMenu}>Journal</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink to="/OurStory" onClick={closeMenu}>Our Story</MobileNavLink>
            </MobileNavItem>
            <MobileNavItem>
              <MobileNavLink to="/contact" onClick={closeMenu}>Contact</MobileNavLink>
            </MobileNavItem>
            
            <MobileAuthSection>
              <SignInLink to="/auth/login" onClick={closeMenu} style={{ width: '100%' }}>SIGN IN</SignInLink>
              <JoinButton to="/auth/partner-signup" onClick={closeMenu} style={{ width: '100%' }}>JOIN AS PARTNER</JoinButton>
            </MobileAuthSection>
          </MobileNavLinks>
        </MobileMenu>
      </NavContainer>
    </NavBar>
  );
};

export default Navbar; 